import {
    EzException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';
import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzWantEventSettings } from '/ezlibrary/entities/EzWantEventSettings.js';
import { EzEventData } from '/ezlibrary/entities/EzEventData.js';
import { EzWantEventDef } from '/ezlibrary/events/EzWantEventDef.js';
import { EzWantElementEventDef } from '/ezlibrary/events/EzWantElementEventDef.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';

/**
 * TODO: Move this class to /ezlibrary/events/EzEventEngine.js and update import references.
 * @class
 * @extends {EzClass}
 * @description
 * ezClocker UX Event Engine
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Check if singleton instance is ready:
 *     globalThis.ezApi.ezclocker?.[EzEventEngine.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen for singleton instance onReady event with:
 *     EzClass.ezWaitReady(
 *         EzEventEngine.ezEventNames.onReady,
 *         {listening class name}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * How to register a event for others to listen to:
 *      Template:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
 *             <name_of_class_that_triggers_event>,
 *             <event_name>);
 *
 *      Example:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
 *              <event_owner_class>.ezApiName,
 *              <event_owner_class>.ezEventNames.<event_name>);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * How to Trigger a Event:
 *     Template:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
 *              <name_of_registered_event_to_trigger>,
 *              <data to send with event>);
 *
 *     Example:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
 *              <EzMyClass>.ezEventNames.<onEventNameToTrigger>,
 *              globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
 *                  <EzMyClass>.ezApiName,
 *                 "Event triggereed statement",
 *                 <data to send with event>));
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * How to listen for a registered event:
 *     Template:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
 *              <event_name>,
 *              <name_of_class_listenting_for_event>,
 *              <reference_to_function_handling_the_event>);
 *
 *      Example:
 *          globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
 *              <event_name>,
 *              <class_listening_for_event>.ezApiName,
 *              <class_listening_for_event>.ezInstance.<method_handling_event>);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * How to listen for an HTML element's event:
 *      globalThis.ezApi.ezclocker.ezeventEngine.ezWantElementEvent(
 *          {elementId},
 *          {event name - use EzElementEventName enum class},
 *          {name of class who created this listener},
 *          {reference to function that will handle the event});
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzEventEngine extends EzClass {
    /**
     * @static
     * @public @function
     * Allows creating a new instance of EzWantElementEventDef without having to import EzWantElementEventDef.
     * @param {string} [elementId=EzString.EMPTY]
     * @param {string} [elementEventName=EzString.EMPTY]
     * @param {function} [elementEventHandlerFunction=null]
     * @returns {EzWantElementEventDef}
     */
    static ezCreateEzWantElementEventDef(elementId = EzString.EMPTY, elementEventName = EzString.EMPTY, elementEventHandlerFunction = null) {
        if (!EzString.hasText(elementId)) {
            throw new EzBadParamException(
                'elementId',
                EzEventEngine,
                EzEventEngine.ezCreateWantElementEventDef);
        }
        if (!EzString.hasText(elementEventName)) {
            throw new EzBadParamException(
                'elementEventName',
                EzEventEngine,
                EzEventEngine.ezCreateWantElementEventDef);
        }
        if (!EzFunction.isFunction(elementEventHandlerFunction)) {
            throw new EzBadParamException(
                'elementEventHandlerFunction',
                EzEventEngine,
                EzEventEngine.ezCreateWantElementEventDef);
        }

        return new EzWantElementEventDef(
            elementId,
            elementEventName,
            elementEventHandlerFunction);
    }

    /**`
     * @public @function
     * Allows creating a new instance of EzWantElementEventDef without having to import EzWantElementEventDef.
     * @param {string} [eventName=EzString.EMPTY]
     * @param {function} [eventHandlerFunction=null]
     * @param {undefined|null|boolean} [immediateTriggerIfAlreadyTriggered=false]
     * @param {undefined|null|boolean} [unwantAfterFirstTrigger=false]
     * @returns {EzWantEventDef}
     */
    static ezCreateEzWantEventDef(eventName = EzString.EMPTY, eventHandlerFunction = null, immediateTriggerIfAlreadyTriggered = false,
        unwantAfterFirstTrigger = false) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine,
                EzEventEngine.ezCreateEzWantEventDef);
        }
        if (!EzFunction.isFunction(eventHandlerFunction)) {
            throw new EzBadParamException(
                'eventHandlerFunction',
                EzEventEngine,
                EzEventEngine.ezCreateEzWantEventDef);
        }

        return new EzWantEventDef(
            eventName,
            eventHandlerFunction,
            EzBoolean.booleanOrFalse(immediateTriggerIfAlreadyTriggered),
            EzBoolean.booleanOrFalse(unwantAfterFirstTrigger));
    }

    /**
     * @private @field
     * Stores an object that contains the registered events name information
     * @type {object}
     */
    #ezRegisteredEventNames = EzObject.EMPTY;
    /**
     * @public @property @getter
     * Gets an object that contains the registered events name information
     * @returns {object}
     */
    get ezRegisteredEventNames() {
        return this.#ezRegisteredEventNames;
    }
    /**
     * @public @property @setter
     * Sets an object that contains the registered events name information
     * @param {object} registeredEventNames
     */
    set ezRegisteredEventNames(registeredEventNames) {
        this.#ezRegisteredEventNames = EzObject.assignOrEmpty(registeredEventNames);
    }

    /**
     * @private @field
     * Stores an object that contains the want event registrations.
     * @type {object}
     */
    #ezWantEventRegistrations = EzObject.EMPTY;
    /**
     * @public @property @getter
     * Gets an object that contains the want event registrations.
     * @returns {object}
     */
    get ezWantEventRegistrations() {
        return this.#ezWantEventRegistrations;
    }
    /**
     * @public @property @setter
     * Sets an object that contains the want event registrations.
     * @param {object} wantEventRegistrations
     */
    set ezWantEventRegistrations(wantEventRegistrations) {
        this.#ezWantEventRegistrations = EzObject.assignOrEmpty(wantEventRegistrations);
    }

    /**
     * @private @field
     * Stores an object that contains the want event properties
     * @type {object}
     */
    #ezWantEventProperties = EzObject.EMPTY;
    /**
     * @public @property @getter
     * Gets an object that contains the want event properties
     * @returns {object}
     */
    get ezWantEventProperties() {
        return this.#ezWantEventProperties;
    }
    /**
     * @public @property @setter
     * Sets an object that contains the want event properties
     * @param {object} wantEventProperties
     */
    set ezWantEventProperties(wantEventProperties) {
        this.#ezWantEventProperties = EzObject.assignOrEmpty(wantEventProperties);
    }

    /**
     * @public @readonly @property
     * Getse class static EzEventEngine.ezEventNames
     * @returns {object}
     * @deprecated
     * Migrate to using the static reference instead: EzEventEngine.ezEventNames
     */
    get ezEventNames() {
        return EzEventEngine.ezEventNames;
    }

    /**
     * @public @field
     * Stores if EzEventEngine is in debug mode or not
     * Default: false
     * @type {boolean}
     */
    #ezDebugMode = false;
    /**
     * @public @property @getter
     * Gets if EzEventEngine is in debug mode or not
     * @returns {boolean}
     */
    get ezDebugMode() {
        return this.#ezDebugMode;
    }
    /**
     * @public @property @setter
     * Sets if EzEventEngine is in debug mode or not
     * @param {boolean} debugMode
     * Default: false
     */
    set ezDebugMode(debugMode = false) {
        this.#ezDebugMode = EzBoolean.booleanOrFalse(debugMode);

        if (EzBoolean.isTrue(this.#ezDebugMode)) {
            EzEventEngine.ezInstance.ezWantEvent(
                EzEventEngine.ezEventNames.onReady,
                EzEventEngine.ezApiName,
                this.ezDebugLogInternalEvents);

            EzEventEngine.ezInstance.ezWantEvent(
                EzEventEngine.ezEventNames.onEventRegistered,
                EzEventEngine.ezApiName,
                this.ezDebugLogInternalEvents);

            EzEventEngine.ezInstance.ezWantEvent(
                EzEventEngine.ezEventNames.onWantEventRegistered,
                EzEventEngine.ezApiName,
                this.ezDebugLogInternalEvents);

            EzEventEngine.ezInstance.ezWantEvent(
                EzEventEngine.ezEventNames.onUnwantEventProcessed,
                EzEventEngine.ezInstance.ezApiName,
                this.ezDebugLogInternalEvents);
        } else {
            EzEventEngine.ezInstance.ezUnwantEvent(
                EzEventEngine.ezEventNames.onReady,
                EzEventEngine.ezApiName);

            EzEventEngine.ezInstance.ezUnwantEvent(
                EzEventEngine.ezEventNames.onEventRegistered,
                EzEventEngine.ezApiName);

            EzEventEngine.ezInstance.ezUnwantEvent(
                EzEventEngine.ezEventNames.onWantEventRegistered,
                EzEventEngine.ezApiName);

            EzEventEngine.ezInstance.ezUnwantEvent(
                EzEventEngine.ezEventNames.onUnwantEventProcessed,
                EzEventEngine.ezApiName);
        }
    }

    /**
     * @public @function
     * Initializes the EzEventEngine
     * @returns {EzEventEngine}
     */
    ezInit() {
        EzEventEngine.ezInstance.ezRegisterEvent(
            EzEventEngine.ezApiName,
            EzEventEngine.ezEventNames.onEventRegistered);

        EzEventEngine.ezInstance.ezRegisterEvent(
            EzEventEngine.ezApiName,
            EzEventEngine.ezEventNames.onReady);

        EzEventEngine.ezInstance.ezRegisterEvent(
            EzEventEngine.ezApiName,
            EzEventEngine.ezEventNames.onWantEventRegistered);

        EzEventEngine.ezInstance.ezRegisterEvent(
            EzEventEngine.ezApiName,
            EzEventEngine.ezEventNames.onUnwantEventProcessed);

        EzEventEngine.ezInstance.ezWantEventRegistrations[EzEventEngine.ezEventNames.onReady] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventRegistrations[EzEventEngine.ezEventNames.onEventRegistered] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventRegistrations[EzEventEngine.ezEventNames.onWantEventRegistered] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventRegistrations[EzEventEngine.ezEventNames.onUnwantEventProcessed] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventProperties[EzEventEngine.ezEventNames.onReady] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventProperties[EzEventEngine.ezEventNames.onEventRegistered] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventProperties[EzEventEngine.ezEventNames.onWantEventRegistered] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezWantEventProperties[EzEventEngine.ezEventNames.onUnwantEventProcessed] = EzObject.EMPTY;

        EzEventEngine.ezInstance.ezDebugMode = false;

        return EzEventEngine.ezInstance;
    }

    /**
     * @public @function
     * Bulk registration of event names for an event owner.
     * @param {string} [eventOwnerName=EzString.EMPTY]
     * @param {array} [eventNamesToRegister=EzArray.EMPTY]
     * Array of event names (as a string)
     */
    ezRegisterEvents(eventOwnerName = EzString.EMPTY, eventNamesToRegister = EzArray.EMPTY) {
        if (!EzString.hasText(eventOwnerName)) {
            throw new EzBadParamException(
                'eventOwnerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezRegisterEvents);
        }
        if (!EzArray.hasLength(eventNamesToRegister)) {
            throw new EzBadParamException(
                'eventNamesToRegister',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezRegisterEvents);
        }

        for (const eventNameToRegister of eventNamesToRegister) {
            EzEventEngine.ezInstance.ezRegisterEvent(
                eventOwnerName,
                eventNameToRegister);
        }
    }

    /**
     * @public @function
     * Registers an event for triggering
     * @param {string} [eventOwner=EzString.EMPTY]
     * @param {string} [eventName=EzString.EMPTY]
     */
    ezRegisterEvent(eventOwner = EzString.EMPTY, eventName = EzString.EMPTY) {
        if (!EzString.hasText(eventOwner)) {
            throw new EzBadParamException(
                'eventOwner',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezRegisterEvent);
        }
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezRegisterEvent);
        }

        const registeredEvent = {
            owner: eventOwner,
            name: eventName,
            ezTriggerCount: EzNumber.ZERO,
            ezLastTriggerEventData: null
        };

        EzEventEngine.ezInstance.ezRegisteredEventNames[eventName] = registeredEvent;

        if (EzEventEngine.ezInstance.#ezDebugMode) {
            EzEventEngine.ezInstance.ezTriggerEvent(
                EzEventEngine.ezEventNames.onEventRegistered,
                new EzEventData(
                    EzEventEngine.ezApiName,
                    `Registered event name ${eventName} for event owner named ${eventOwner}`,
                    registeredEvent));
        }
    }

    /**
     * @public @function
     * Unregisters an event
     * @param {string} [eventName=EzString.EMPTY]
     */
    ezUnregisterEvent(eventName = EzString.EMPTY) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnregisterEvent);
        }

        if (EzEventEngine.ezInstance.ezIsEventRegistered(eventName)) {
            delete EzEventEngine.ezInstance.ezRegisteredEventNames[eventName];
        }
    }

    /**
     * @public @function
     * Determines if an event with the provided name is already registerd or not.
     * It is possible to have multiple events with the same name registered using different
     * event owner names. This method returns true with the first eventName match.
     * @param {string} [eventName=EzString.EMPTY]
     * @returns {boolean}
     */
    ezIsEventRegistered(eventName = EzString.EMPTY) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezIsEventRegistered);
        }

        return EzObject.isValid(EzEventEngine.ezInstance.ezRegisteredEventNames?.eventName);
    }

    /**
     * @public @function
     * Bulk registration of a event handler's want events.
     * @param {string} [eventHandlerName=EzString.EMPTY]
     * @param {array} [ezWantEventDefs=EzArray.EMPTY]
     * An array of EzWantEventDef instances.
     */
    ezWantEvents(eventHandlerName = EzString.EMPTY, ezWantEventDefs = EzArray.EMPTY) {
        if (!EzString.hasText(eventHandlerName)) {
            throw new EzBadParamException(
                'eventHandlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEvents);
        }
        if (!EzArray.hasLength(ezWantEventDefs)) {
            throw new EzBadParamException(
                'ezWantEventDefs',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEvents);
        }

        for (const ezWantEventDef of ezWantEventDefs) {
            EzEventEngine.ezInstance.ezWantEvent(
                ezWantEventDef.ezEventName,
                eventHandlerName,
                ezWantEventDef.ezEventHandlerFunction,
                ezWantEventDef.ezImmediateTriggerIfAlreadyTriggered,
                ezWantEventDef.ezUnwantAfterFirstTrigger);
        }
    }

    /**
     * @public @function
     * Registers an event handler named eventHandlerName for event eventName to execute function
     * eventHandlerFunction when eventName is triggered.
     * @param {string} [eventName=EzString.EMPTY]
     * @param {string} [handlerName=EzString.EMPTY]
     * @param {function} [handlerFunction=EzFunction.EMPTY]
     * @param {undefined|null|boolean} [immediateTriggerIfAlreadyTriggered=false]
     * Default is false
     * @param {undefined|null|boolean} [unwantAfterFirstTrigger=false]
     * Default is false'
     */
    ezWantEvent(eventName = EzString.EMPTY, handlerName = EzString.EMPTY, handlerFunction = EzFunction.EMPTY, immediateTriggerIfAlreadyTriggered = false,
        unwantAfterFirstTrigger = false) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEvent);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEvent);
        }
        if (!EzFunction.isFunction(handlerFunction)) {
            throw new EzBadParamException(
                'handlerFunction',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEvent);
        }

        EzEventEngine.ezInstance.ezWantEventEx(
            EzEventEngine.ezInstance.ezCreateWantEventSettings(
                eventName,
                handlerName,
                handlerFunction,
                EzBoolean.booleanOrFalse(immediateTriggerIfAlreadyTriggered),
                EzBoolean.booleanOrFalse(unwantAfterFirstTrigger)));
    }

    /**
     * @public @function
     * Registers an event handler named eventHandlerName for event eventName to execute function
     * eventHandlerFunction when eventName is triggered.
     * @param {object} [wantEventSettings=null]
     * Expected object:
     *  {
     *      eventName: {string},
     *      handlerName: {string},
     *      handlerFunction: {function}
     *  }
     */
    ezWantEventEx(wantEventSettings = null) {
        if (!EzObject.isValid(wantEventSettings)) {
            throw new EzBadParamException(
                'wantEventSettings',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEventEx);
        }
        if (!EzString.hasText(wantEventSettings?.eventName)) {
            throw new EzBadParamException(
                'wantEventSettings.eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEventEx);
        }
        if (!EzString.hasText(wantEventSettings?.handlerName)) {
            throw new EzBadParamException(
                'wantEventSettings.handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEventEx);
        }
        if (!EzFunction.isFunction(wantEventSettings?.handlerFunction)) {
            throw new EzBadParamException(
                'wantEventSettings.handlerFunction',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantEventEx);
        }

        if (!EzObject.isValid(EzEventEngine.ezInstance.ezWantEventRegistrations?.[wantEventSettings.eventName])) {
            EzEventEngine.ezInstance.ezWantEventRegistrations[wantEventSettings.eventName] = EzObject.EMPTY;
        }

        EzEventEngine.ezInstance.ezWantEventRegistrations[wantEventSettings.eventName][wantEventSettings.handlerName] = wantEventSettings.handlerFunction;

        EzEventEngine.ezInstance.ezAddWantEventProperties(wantEventSettings);

        if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
            EzEventEngine.ezInstance.ezTriggerEvent(
                EzEventEngine.ezEventNames.onWantEventRegistered,
                new EzEventData(
                    EzEventEngine.ezApiName,
                    `A want event was registerd with settings ${EzJson.toJson(wantEventSettings)}.`));
        }

        if (EzObject.isValid(wantEventSettings?.options) &&
            EzBoolean.isTrue(wantEventSettings.options?.immediateTriggerIfAlreadyTriggered) &&
            EzObject.isValid(EzEventEngine.ezInstance.ezRegisteredEventNames?.wantEventSettings?.eventName) &&
            EzNumber.ZERO !== EzEventEngine.ezInstance.ezRegisteredEventNames?.[wantEventSettings.eventName]?.ezTriggerCount) {

            // Immediatly call the event since it has already triggered at least once
            EzFunction.callback(
                wantEventSettings.handlerFunction,
                EzObject.assignOrNull(
                    EzEventEngine.ezInstance.ezRegisteredEventNames[wantEventSettings.eventName]?.ezLastTriggerEventData,
                    null));

            EzEventEngine.ezInstance.ezPostTrigger(
                wantEventSettings.eventName,
                wantEventSettings.handlerName);
        }
    }

    /**
     * @public @function
     * Triggers an event with eventName with data eventData.
     * @param {string} [eventName=EzString.EMPTY]
     * @param {undefined|null|object} [eventData=null]
     * @param {undefined|null|object} [thisToApply=this]
     * Default: this
     */
    ezTriggerEvent(eventName = EzString.EMPTY, eventData = null, thisToApply = this) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezTriggerEvent);
        }

        const triggeringEvent = EzObject.assignOrNull(EzEventEngine.ezInstance.ezRegisteredEventNames?.[eventName]);

        if (!EzObject.isValid(triggeringEvent)) {
            if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.error(`EzEventEngine did not trigger unregistered event "${eventName}"`);
            }

            return;
        }

        if (!EzString.hasText(triggeringEvent?.name)) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                `EzEventEngine did not trigger event "${eventName}" due to the event's name property returning a undefined, null, or empty name.`);

            return;
        }

        const wantEvents = EzEventEngine.ezInstance.ezWantEventRegistrations?.[triggeringEvent.name];

        triggeringEvent.triggerCount++;

        EzEventEngine.ezInstance.ezRegisteredEventNames[eventName].ezTriggerCount++;

        EzEventEngine.ezInstance.ezRegisteredEventNames[eventName].ezLastTriggerEventData = eventData;

        if (!EzObject.isValid(wantEvents) || EzNumber.ZERO === Object.keys(wantEvents).length) {
            if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.warn(
                    `No listeners registered for event ${triggeringEvent.name} at trigger time with eventData=${EzJson.toJson(eventData)}`);
            }

            return;
        }

        for (let eventHandlerName in wantEvents) {
            let eventHandlerFunction = wantEvents[eventHandlerName];

            if (EzFunction.isFunction(eventHandlerFunction)) {
                // Call the handler function
                EzFunction.callWithParams(
                    eventHandlerFunction,
                    [eventData],
                    EzObject.assignOrDefault(
                        thisToApply,
                        this));

                EzEventEngine.ezInstance.ezPostTrigger(
                    eventName,
                    eventHandlerName);
            }
        }
    }

    /**
     * @public @function
     * Removes a handler from the want event listener queue
     * @param {string} [eventName=EzString.EMPTY]
     * @param {string} [handlerName=EzString.EMPTY]
     */
    ezUnwantEvent(eventName = EzString.EMPTY, handlerName = EzString.EMPTY) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnwantEvent);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnwantEvent);
        }

        if (EzObject.isValid(EzEventEngine.ezInstance.ezWantEventRegistrations?.[eventName]?.[handlerName])) {
            // Remove the handler from the want events
            delete EzEventEngine.ezInstance.ezWantEventRegistrations[eventName][handlerName];
        }

        if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
            EzEventEngine.ezInstance.ezTriggerEvent(
                EzEventEngine.ezEventNames.onUnwantEventProcessed,
                new EzEventData(
                    EzEventEngine.ezApiName,
                    `The event handler ${handlerName} is now disconnected from event ${eventName}.`));
        }
    }

    /**
     * @public @function
     * Handles bulk registration of element events
     * @param {string} [handlerName=EzString.EMPTY]
     * @param {array} [wantElementEventDefs=EzArray.EMPTY]
     * An array of EzWantElementEventDef instances.
     */
    ezWantElementEvents(handlerName = EzString.EMPTY, wantElementEventDefs = EzArray.EMPTY) {
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvents);
        }
        if (!EzArray.hasLength(wantElementEventDefs)) {
            throw new EzBadParamException(
                'wantElementEventDefs',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvents);
        }

        for (const ezWantElementEventDef of wantElementEventDefs) {
            if (EzString.hasText(ezWantElementEventDef?.ezElementId) && EzString.hasText(ezWantElementEventDef.ezElementEventName) &&
                EzFunction.isFunction(ezWantElementEventDef.ezElementEventHandlerFunction)) {
                EzEventEngine.ezInstance.ezWantElementEvent(
                    ezWantElementEventDef.ezElementId,
                    ezWantElementEventDef.ezElementEventName,
                    handlerName,
                    ezWantElementEventDef.ezElementEventHandlerFunction);
            } else if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.warn(
                    EzString.em`
                        Ignored want element event definition due to missing or invalid properties.
                        [ezWantElementEventDef: ${EzJson.toJson(ezWantElementEventDef)}]`);
            }
        }
    }

    /**
     * @public @function
     * Hooks an HTML element's event fire to the provided handlerFunction.
     * @param {object|string} [elementOrId=null]
     * The element or its ID
     * @param {string} [elementEvent=EzString.EMPTY]
     * The event name
     * @param {string} [handlerName=EzString.EMPTY]
     * The event handler name
     * @param {function} [handlerFunction=null]
     * The event handler function
     * @returns {null|object}
     * Returns the element (if any)
     */
    ezWantElementEvent(elementOrId = null, elementEvent = EzString.EMPTY, handlerName = EzString.EMPTY, handlerFunction = null) {
        if (!EzObject.isValid(elementOrId) || EzString.isString(elementOrId) && !EzString.hasText(elementOrId)) {
            throw new EzBadParamException(
                'elementOrId',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvent);
        }
        if (!EzString.hasText(elementEvent)) {
            throw new EzBadParamException(
                'elementEvent',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvent);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvent);
        }
        if (!EzFunction.isFunction(handlerFunction)) {
            throw new EzBadParamException(
                'handlerFunction',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWantElementEvent);
        }

        const element = EzEventEngine.ezInstance.ezFindByElementOrId(elementOrId);

        if (!EzObject.isValid(element)) {
            if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    EzString.em`
                        Unable to hook event for a missing or non-existing HTML element.
                        ElementOrId=${elementOrId}, elementEvent=${elementEvent}, handlerName=${handlerName}, handlerFunction=${handlerFunction}`);
            }

            return null;
        }

        if (!EzString.hasText(element?.id)) {
            element.id = EzEventEngine.ezInstance.ezGenerateMissingElementId(element, handlerName);
        }

        const wrappedEventName = `${element.id}_${elementEvent}`;

        if (EzBoolean.isFalse(EzEventEngine.ezInstance.ezIsEventRegistered(wrappedEventName))) {
            EzEventEngine.ezInstance.ezWrapElementEvent(
                element.id,
                elementEvent,
                EzEventEngine.ezApiName,
                wrappedEventName);
        }

        EzEventEngine.ezInstance.ezWantEvent(
            wrappedEventName,
            handlerName,
            handlerFunction,
            false);

        return element;
    }

    /**
     * @public @function
     * Generates an ID for an element that doesn't have an id
     * @param {object} [element=null]
     * @param {string} [handlerName=EzString.EMPTY]
     * @returns {string}
     */
    ezGenerateMissingElementId(element = null, handlerName = EzString.EMPTY) {
        if (!EzObject.isValid(element)) {
            throw new EzBadParamException(
                'element',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezGenerateMissingElementId);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezGenerateMissingElementId);
        }

        const elementName = EzString.stringWithTextOrDefault(
            element?.constructor?.name?.toUpperCase(),
            'UNKNOWN_ELEMENT');

        return `EzAutoId${elementName}_${handlerName.toUpperCase()}`;
    }

    /**
     * @public @function
     * Hooks an HTML element's event fire to the provided handlerFunction.
     * @param {object|string} [elementOrId=null]
     * The element or its ID
     * @param {string} [eventName=EzString.EMPTY]
     * The name of the event
     * @param {string} [handlerName=EzString.EMPTY]
     * The event handler name
     * @returns {undefined|null|object}
     * Returns the element (if any)
     */
    ezUnwantElementEvent(elementOrId = null, elementEvent = EzString.EMPTY, handlerName = EzString.EMPTY) {
        if (!EzObject.isValid(elementOrId) || EzString.isString(elementOrId) && !EzString.hasText(elementOrId)) {
            throw new EzBadParamException(
                'elementOrId',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnwantElementEvent);
        }
        if (!EzString.hasText(elementEvent)) {
            throw new EzBadParamException(
                'elementEvent',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnwantElementEvent);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezUnwantElementEvent);
        }

        const element = EzEventEngine.ezInstance.ezFindByElementOrId(elementOrId);

        if (!EzObject.isValid(element)) {
            if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    EzString.em`
                        Unable to disconnect ${handlerName}'s event listener
                        from event ${elementEvent} on an HTML element that does not exist.`);
            }

            return null;
        }

        if (!EzString.hasText(element?.id)) {
            element.id = EzEventEngine.ezInstance.ezGenerateMissingElementId(element, handlerName);
        }

        const wrappedEventName = `${element.id}_${elementEvent}`;

        EzEventEngine.ezInstance.ezUnwantEvent(wrappedEventName, handlerName);

        if (EzNumber.ZERO === EzEventEngine.ezInstance.ezWantEventRegistrations?.[wrappedEventName]?.length) {
            EzEventEngine.ezInstance.ezUnregisterEvent(wrappedEventName);
        }

        return element;
    }

    /**
     * @public @function
     * Performs post event trigger operations
     * @param {string} [eventName=EzString.EMPTY]
     * @param {string} [eventHandlerName=EzString.EMPTY]
     */
    ezPostTrigger(eventName = EzString.EMPTY, eventHandlerName = EzString.EMPTY) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezPostTrigger);
        }
        if (!EzString.hasText(eventHandlerName)) {
            throw new EzBadParamException(
                'eventHandlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezPostTrigger);
        }

        if (!EzObject.isValid(EzEventEngine.ezInstance.ezWantEventProperties?.[eventName]?.[eventHandlerName])) {
            return;
        }

        if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezWantEventProperties[eventName][eventHandlerName]?.ezUnwantAfterFirstTrigger)) {
            EzEventEngine.ezInstance.ezUnwantEvent(eventName, eventHandlerName);

            delete EzEventEngine.ezInstance.ezWantEventProperties[eventName][eventHandlerName];
        } else {
            EzEventEngine.ezInstance.ezWantEventProperties[eventName][eventHandlerName].ezTriggerCount = EzNumber.numberOrZero(
                EzEventEngine.ezInstance.ezWantEventProperties[eventName][eventHandlerName]?.ezTriggerCount) + EzNumber.ONE;
        }
    }

    /**
     * @public @function
     * Adds additional properties for a want event call
     * @param {object} wantEventSettings
     */
    ezAddWantEventProperties(wantEventSettings = null) {
        if (!EzObject.isValid(wantEventSettings)) {
            throw new EzBadParamException(
                'wantEventSettings',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezAddWantEventProperties);
        }
        if (!EzString.hasText(wantEventSettings.eventName)) {
            throw new EzBadParamException(
                'wantEventSettings.eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezAddWantEventProperties);
        }
        if (!EzString.hasText(wantEventSettings.handlerName)) {
            throw new EzBadParamException(
                'wantEventSettings.handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezAddWantEventProperties);
        }

        if (!EzObject.isValid(EzEventEngine.ezInstance.ezWantEventProperties?.[wantEventSettings.eventName])) {
            EzEventEngine.ezInstance.ezWantEventProperties[wantEventSettings.eventName] = EzObject.EMPTY;
        }

        EzEventEngine.ezInstance.ezWantEventProperties[wantEventSettings.eventName][wantEventSettings.handlerName] = wantEventSettings;
    }

    /**
     * @public @function
     * Obtains the want event properties for the provided event name + handler name combo.
     * @param {string} [eventName=EzString.EMPTY]
     * @param {string} [handlerName=EzString.EMPTY]
     * @returns {object}
     */
    ezGetWantEventProperties(eventName, handlerName) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezGetWantEventProperties);
        }
        if (!EzString.hasText(handlerName)) {
            throw new EzBadParamException(
                'handlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezGetWantEventProperties);
        }

        if (!EzObject.isValid(EzEventEngine.ezInstance.ezWantEventProperties?.[eventName]?.[handlerName])) {
            EzEventEngine.ezInstance.ezAddWantEventProperties(
                EzEventEngine.ezInstance.ezCreateWantEventSettings(
                    eventName,
                    handlerName,
                    false,
                    false));
        }

        return EzEventEngine.ezInstance.ezWantEventProperties[eventName][handlerName];
    }

    /**
     * @public @function
     * Builds the event data for an ezEventEngine trigger
     * @param {object} [triggeredByRef=null]
     * @param {undefined|null|string} [message=EzString.EMPTY]
     * @param {undefined|null|object} [data=null]
     * @returns {EzEventData}
     * @deprecated
     * Migrate to directly creating a new EzEventData: new EzEventData(triggeredByRef, message, data);
     */
    ezBuildEventData(triggeredByRef = null, message = EzString.EMPTY, data = null) {
        if (!EzObject.isValid(triggeredByRef)) {
            throw new EzBadParamException(
                'triggeredByRef',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezBuildEventData);
        }

        return new EzEventData(
            triggeredByRef,
            EzString.stringWithTextOrEmpty(message),
            EzObject.assignOrNull(data));
    }

    /**
     * @public @function
     * Removes a handler from the want event listener queue
     * @param {string} eventName
     */
    ezClearAllWantEvents(eventName = EzString.EMPTY) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezClearAllWantEvents);
        }

        if (EzObject.isValid(EzEventEngine.ezInstance.ezWantEventRegistrations?.[eventName])) {
            // Remove the handler from the want events
            for (const handlerName in EzEventEngine.ezInstance.ezWantEventRegistrations[eventName]) {
                if (EzObject.isValid(EzEventEngine.ezInstance.ezWantEventRegistrations[eventName]?.[handlerName])) {
                    EzEventEngine.ezInstance.ezUnwantEvent(eventName, handlerName);
                }
            }
        }
    }

    /**
     * @public @function
     * Clears all the registered want events for eventName from the element with the provided elementId.
     * @param {string} [elementId=EzString.EMPTY]
     * @param {string} [eventName=EzString.EMPTY]
     */
    ezClearAllWantElementEventsForElementId(elementId = EzString.EMPTY, eventName = EzString.EMPTY) {
        if (!EzString.hasText(elementId)) {
            throw new EzBadParamException(
                'elementId',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezClearAllWantElementEventsForElementId);
        }
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezClearAllWantElementEventsForElementId);
        }

        const wrappedEventName = `${elementId}_${eventName}`;

        EzEventEngine.ezInstance.ezUnwantEvent(wrappedEventName);

        if (EzObject.isValid(EzEventEngine.ezInstance.ezWantEventRegistrations?.[wrappedEventName])) {
            // Remove the handler from the want events
            delete EzEventEngine.ezInstance.ezWantEventRegistrations[wrappedEventName];
        }

        EzEventEngine.ezInstance.ezUnregisterEvent(wrappedEventName);
    }

    /**
     * @public @function
     * Clears all the registered want events for eventName from the element who's id starts with the provided elementIdStartsWith.
     * @param {string} [elementIdStartsWith=EzString.EMPTY]
     * @param {string} [eventName=EzString.EMPTY]
     */
    ezClearAllWantElementEventsForElementIdStartsWith(elementIdStartsWith = EzString.EMPTY, eventName = EzString.EMPTY) {
        if (!EzString.hasText(elementIdStartsWith)) {
            throw new EzBadParamException(
                'elementIdStartsWith',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezClearAllWantElementEventsForElementIdStartsWith);
        }
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezClearAllWantElementEventsForElementIdStartsWith);
        }

        for (const aEventName in EzEventEngine.ezInstance.ezWantEventRegistrations) {
            if (aEventName.startsWith(elementIdStartsWith, EzNumber.ZERO)) {
                EzEventEngine.ezInstance.ezUnregisterEvent(aEventName);

                delete EzEventEngine.ezInstance.ezWantEventRegistrations[aEventName];
            }
        }
    }

    /**
     * @public @function
     * Wraps an HTML element event with an ezEventEngine event.
     * @param {string|object} [elementOrId=null]
     * @param {string} [elementEvent=EzString.EMPTY]
     * @param {string} [eventOwner=EzString.EMPTY]
     * @param {string} [wrappedEventName=EzString.EMPTY]
     */
    ezWrapElementEvent(elementOrId = null, elementEvent = EzString.EMPTY, eventOwner = EzString.EMPTY, wrappedEventName = EzString.EMPTY) {
        if (!EzObject.isValid(elementOrId) || EzString.isString(elementOrId) && !EzString.hasText(elementOrId)) {
            throw new EzBadParamException(
                'elementOrId',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWrapElementEvent);
        }
        if (!EzString.hasText(elementEvent)) {
            throw new EzBadParamException(
                'elementEvent',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWrapElementEvent);
        }
        if (!EzString.hasText(eventOwner)) {
            throw new EzBadParamException(
                'eventOwner',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWrapElementEvent);
        }
        if (!EzString.hasText(wrappedEventName)) {
            throw new EzBadParamException(
                'wrappedEventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezWrapElementEvent);
        }

        const element = EzEventEngine.ezInstance.ezFindByElementOrId(elementOrId);

        if (!EzObject.isValid(element)) {
            if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    EzString.em`
                        Unable to wrap element event for a missing or non-existing HTML element.
                        ElementOrId=${elementOrId}, elementEvent=${elementEvent}, eventOwner=${eventOwner}, wrappedEventName=${wrappedEventName}`);
            }

            return;
        }

        if (!(elementEvent in element)) {
            throw new EzException(`The HTML element does not support event named '${elementEvent}'.`);
        }

        element[elementEvent] = (event) => EzEventEngine.ezInstance.ezTriggerEvent(
            wrappedEventName,
            new EzEventData(
                eventOwner,
                elementEvent,
                {
                    elementId: element.id,
                    elementEvent: event
                }));

        EzEventEngine.ezInstance.ezRegisterEvent(eventOwner, wrappedEventName);
    }

    /**
     * @public @function
     * Builds the EzEventEngine.prototype.ezWantEventEx wantEventSettings param object
     * @param {string} [eventName=EzString.EMPTY]
     * @param {string} [eventHandlerName=EzString.EMPTY]
     * @param {function} [eventHandlerFunction=null]
     * @param {undefined|null|boolean} [immediateTriggerIfAlreadyTriggered=false]
     * Default is false
     * @param {undefined|null|boolean} [unwantAfterFirstTrigger=false]
     * Default is false
     * @returns {EzWantEventSettings}
     */
    ezCreateWantEventSettings(eventName = EzString.EMPTY, eventHandlerName = EzString.EMPTY, eventHandlerFunction = null,
        immediateTriggerIfAlreadyTriggered = false, unwantAfterFirstTrigger = false) {
        if (!EzString.hasText(eventName)) {
            throw new EzBadParamException(
                'eventName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezCreateWantEventSettings);
        }
        if (!EzString.hasText(eventHandlerName)) {
            throw new EzBadParamException(
                'eventHandlerName',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezCreateWantEventSettings);
        }
        if (!EzFunction.isFunction(eventHandlerFunction)) {
            throw new EzBadParamException(
                'eventHandlerFunction',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezCreateWantEventSettings);
        }

        return new EzWantEventSettings(
            eventName,
            eventHandlerName,
            eventHandlerFunction,
            {
                immediateTriggerIfAlreadyTriggered: EzBoolean.booleanOrFalse(immediateTriggerIfAlreadyTriggered),
                unwantAfterFirstTrigger: EzBoolean.booleanOrFalse(unwantAfterFirstTrigger)
            });
    }

    /**
     * @public @function
     * Finds the HTML element associated with the provided id, or null if it does not exist.
     * @param {object|string} [elementOrId=null]
     * Acceptable values: 'window', 'document', 'body', 'head', the string id for an HTML element, an actual HTML element reference.
     * @returns {object}
     * Returns the element who's id is elementOrId OR returns elementOrId (as it is assumed to be the actual element reference)
     */
    ezFindByElementOrId(elementOrId = null) {
        if (!EzObject.isValid(elementOrId) || EzString.isString(elementOrId) && !EzString.hasText(elementOrId)) {
            // Element not valid
            throw new EzBadParamException(
                'elementOrId',
                EzEventEngine.ezInstance,
                EzEventEngine.ezInstance.ezFindByElementOrId);
        }

        if (EzString.hasText(elementOrId)) {
            switch (elementOrId) {
                case 'window':
                    return globalThis.window;
                case 'document':
                    return globalThis.window.document;
                case EzUx.bodyTagName:
                    return globalThis.window.document.body;
                case EzUx.headTagName:
                    return globalThis.window.document.head;
            }

            const element = globalThis.document.getElementById(elementOrId);

            if (!EzObject.isValid(element) && EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode)) {
                globalThis.ezApi.ezclocker.ezLogger.error(`The HTML element with id=${elementOrId} does not exist.`);
            }

            return element;
        }

        return elementOrId;
    }

    /**
     * @public @function
     * Logs registration, want, and unwant event messages to the debug log
     * @param {*} eventData
     */
    ezDebugLogInternalEvents(eventData) {
        if (EzBoolean.isTrue(EzEventEngine.ezInstance.ezDebugMode) && EzString.hasText(eventData?.message)) {
            globalThis.ezApi.ezclocker.ezLogger.debug(eventData.message);
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezEventEngine';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzEventEngine_Ready',
            onEventRegistered: 'ezOn_EzEventEngine_EventRegistered',
            onWantEventRegistered: 'ezOn_EzEventEngine_WantEventRegistered',
            onUnwantEventProcessed: 'ezOn_EzEventEngine_UnwantEventProcessed'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzEventEngine}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]
        ? globalThis.ezApi.ezclocker[EzEventEngine.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of EzEventEngine registered with EzApi (if available yet)
     * @reutrns {EzEventEngine}
     */
    static get ezInstance() {
        return EzEventEngine.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of EzEventEngine registered with EzApi (if available yet)
     * @param {EzEventEngine} ezEventEngine
     */
    static set ezInstance(ezEventEngine) {
        if (null != EzEventEngine.#ezInstance) {
            throw new Error('EzEventEngine\'s singleton instance is already reigstered with EzApi.');
        }

        EzEventEngine.#ezInstance = ezEventEngine;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzEventEngine.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzEventEngine.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if all necessary dependences are ready and therefore it is ok for this class to
     * register it's singleton instance with ezApi.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzEventEngine.ezApiRegistrationState && globalThis?.ezApi?.ready;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzEventEngine.ezInstance && EzRegistrationState.REGISTERED === EzEventEngine.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzEventEngine.#ezCanRegister && !EzEventEngine.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzEventEngine, EzEventEngine.ezApiName);

            globalThis.EzEventEngine = EzEventEngine;

            globalThis.ezEventEngine = EzEventEngine.ezInstance;
        }

        return EzEventEngine.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzEventEngine.ezApiName
     *     2) Property getter EzEventEngine.ezEventNames
     *     3) Property getter EzEventEngine.ezInstance
     *     4) Property setter EzEventEngine.ezInstance
     *     5) Property getter EzEventEngine.ezApiRegistrationState
     *     6) Property setter EzEventEngine.ezApiRegistrationState
     *     7) Property getter EzEventEngine.#ezCanRegister
     *     8) Property getter EzEventEngine.#ezIsRegistered
     *     9) Method EzEventEngine.#ezRegistrator()
     */
    static {
        if (!EzEventEngine.#ezIsRegistered) {
            EzEventEngine.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzEventEngine.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzEventEngine.ezOnEzApiReadyEventName,
                    EzEventEngine.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
